import React, { useState, useRef, useCallback } from 'react';
import { CellData } from '../data/crosswordData';

export const KEYBOARD_ROW_1 = ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'] as const;
export const KEYBOARD_ROW_2 = ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'] as const;
export const KEYBOARD_ROW_3 = ['W', 'X', 'C', 'V', 'B', 'N'] as const;
export const KEYBOARD_AMAZIGH_ROW = ['Ɛ', 'Ɣ', 'Ḥ', 'Ṣ', 'Ṭ', 'Ḍ', 'Č', 'Ǧ', 'Ž', 'Ṛ', 'Ẓ'] as const;

export interface CrosswordGridCellProps {
  r: number;
  c: number;
  cell: CellData | null;
  isCurrentActiveCell: boolean;
  isPartOfActiveWord: boolean;
  currentLetter: string;
  isCellLocked: boolean;
  isCellFullySolved: boolean;
  isIncorrect: boolean;
  onClick: (r: number, c: number) => void;
  cellRef?: React.Ref<HTMLButtonElement> | null;
}

export const CrosswordGridCell = React.memo<CrosswordGridCellProps>(
  ({
    r,
    c,
    cell,
    isCurrentActiveCell,
    isPartOfActiveWord,
    currentLetter,
    isCellLocked,
    isCellFullySolved,
    isIncorrect,
    onClick,
    cellRef,
  }) => {
    if (!cell) {
      return (
        <div
          className="w-full aspect-square pointer-events-none opacity-0"
          aria-hidden="true"
        />
      );
    }

    if (cell.isSpace) {
      return (
        <div
          className={`w-full aspect-square rounded-lg flex items-center justify-center border-2 border-dashed transition-colors select-none ${
            isPartOfActiveWord
              ? 'bg-amber-100/60 border-amber-400 text-amber-800 shadow-2xs'
              : 'bg-stone-100/50 border-stone-300 text-stone-400'
          }`}
          title="Espace entre les mots de la phrase"
        >
          <span className="text-xs font-mono font-black opacity-60">␣</span>
        </div>
      );
    }

    return (
      <button
        ref={isCurrentActiveCell ? cellRef : null}
        type="button"
        onClick={() => onClick(r, c)}
        className={`w-full aspect-square rounded-xl flex items-center justify-center font-mono font-black uppercase transition-colors relative select-none cursor-pointer border-2 text-sm sm:text-base ${
          isIncorrect
            ? isCurrentActiveCell
              ? 'bg-rose-100 text-rose-900 border-rose-500 ring-2 ring-rose-500 scale-105 z-10 shadow-md'
              : 'bg-rose-50 text-rose-700 border-rose-400 shadow-xs'
            : isCurrentActiveCell
            ? isCellLocked
              ? 'bg-emerald-100 text-emerald-950 border-emerald-600 ring-2 ring-amber-500 scale-105 z-10 shadow-md'
              : 'bg-amber-400 text-stone-950 border-amber-600 ring-2 ring-amber-500 scale-105 z-10 shadow-md'
            : isPartOfActiveWord
            ? isCellLocked
              ? 'bg-emerald-100/90 text-emerald-950 border-amber-500 shadow-xs'
              : 'bg-amber-100 text-amber-950 border-amber-400 shadow-xs'
            : isCellLocked || isCellFullySolved
            ? 'bg-emerald-50 text-emerald-900 border-emerald-400 shadow-2xs'
            : currentLetter
            ? 'bg-amber-50/80 text-stone-900 border-amber-300 shadow-2xs'
            : 'bg-[#fffdf9] text-stone-800 border-stone-300 hover:border-amber-400 shadow-2xs'
        }`}
      >
        {cell.number && (
          <span
            className={`absolute top-0.5 left-1 text-[8px] sm:text-[9px] font-mono leading-none pointer-events-none ${
              isCurrentActiveCell && !isCellLocked ? 'text-stone-950 font-black' : 'text-amber-800 font-bold'
            }`}
          >
            {cell.number}
          </span>
        )}

        {isIncorrect && (
          <span className="absolute top-0.5 right-1 text-[8px] sm:text-[9px] text-rose-600 font-bold leading-none pointer-events-none">
            ✕
          </span>
        )}

        {isCellLocked && (
          <span
            className={`absolute top-0.5 right-1 text-[8px] sm:text-[9px] font-bold leading-none pointer-events-none ${
              isCurrentActiveCell ? 'text-stone-950' : 'text-emerald-600'
            }`}
          >
            ✓
          </span>
        )}

        <span className="leading-none">{currentLetter}</span>
      </button>
    );
  }
);

CrosswordGridCell.displayName = 'CrosswordGridCell';

export interface CrosswordVirtualKeyboardProps {
  onInputChar: (char: string) => void;
  onBackspace: () => void;
  onSpace?: () => void;
  isPhrase?: boolean;
}

export const CrosswordVirtualKeyboard = React.memo<CrosswordVirtualKeyboardProps>(
  ({ onInputChar, onBackspace, onSpace, isPhrase }) => {
    const [illuminatedKey, setIlluminatedKey] = useState<string | null>(null);
    const illuminateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const triggerKeyFeedback = useCallback((key: string) => {
      if (illuminateTimerRef.current) {
        clearTimeout(illuminateTimerRef.current);
      }
      setIlluminatedKey(key);
      illuminateTimerRef.current = setTimeout(() => {
        setIlluminatedKey(null);
      }, 120);
    }, []);

    return (
      <div className="flex flex-col gap-1.5 w-full">
        {/* ROW 1: A Z E R T Y U I O P */}
        <div className="flex justify-center gap-1 sm:gap-1.5 w-full">
          {KEYBOARD_ROW_1.map((k) => (
            <button
              key={k}
              type="button"
              onPointerDown={() => triggerKeyFeedback(k)}
              onClick={() => onInputChar(k)}
              className={`flex-1 h-10 sm:h-11 md:h-12 bg-white hover:bg-amber-50 text-stone-900 font-mono font-black text-sm sm:text-base rounded-lg border-2 border-stone-300 hover:border-amber-400 keyboard-key-illuminated flex items-center justify-center cursor-pointer shadow-2xs min-w-0 ${
                illuminatedKey === k ? 'is-pressed' : ''
              }`}
            >
              {illuminatedKey === k && (
                <span className="keyboard-key-callout">{k}</span>
              )}
              <span>{k}</span>
            </button>
          ))}
        </div>

        {/* ROW 2: Q S D F G H J K L M */}
        <div className="flex justify-center gap-1 sm:gap-1.5 w-full">
          {KEYBOARD_ROW_2.map((k) => (
            <button
              key={k}
              type="button"
              onPointerDown={() => triggerKeyFeedback(k)}
              onClick={() => onInputChar(k)}
              className={`flex-1 h-10 sm:h-11 md:h-12 bg-white hover:bg-amber-50 text-stone-900 font-mono font-black text-sm sm:text-base rounded-lg border-2 border-stone-300 hover:border-amber-400 keyboard-key-illuminated flex items-center justify-center cursor-pointer shadow-2xs min-w-0 ${
                illuminatedKey === k ? 'is-pressed' : ''
              }`}
            >
              {illuminatedKey === k && (
                <span className="keyboard-key-callout">{k}</span>
              )}
              <span>{k}</span>
            </button>
          ))}
        </div>

        {/* ROW 3: W X C V B N + (Espace if phrase) + Effacer */}
        <div className="flex justify-center gap-1 sm:gap-1.5 w-full">
          {KEYBOARD_ROW_3.map((k) => (
            <button
              key={k}
              type="button"
              onPointerDown={() => triggerKeyFeedback(k)}
              onClick={() => onInputChar(k)}
              className={`flex-1 h-10 sm:h-11 md:h-12 bg-white hover:bg-amber-50 text-stone-900 font-mono font-black text-sm sm:text-base rounded-lg border-2 border-stone-300 hover:border-amber-400 keyboard-key-illuminated flex items-center justify-center cursor-pointer shadow-2xs min-w-0 ${
                illuminatedKey === k ? 'is-pressed' : ''
              }`}
            >
              {illuminatedKey === k && (
                <span className="keyboard-key-callout">{k}</span>
              )}
              <span>{k}</span>
            </button>
          ))}

          {/* Space button when in a phrase */}
          {isPhrase && onSpace && (
            <button
              type="button"
              onPointerDown={() => triggerKeyFeedback('space')}
              onClick={onSpace}
              className={`flex-[1.4] h-10 sm:h-11 md:h-12 bg-amber-50 hover:bg-amber-100 text-amber-900 font-black text-xs sm:text-sm rounded-lg border-2 border-amber-300 keyboard-key-illuminated flex items-center justify-center gap-1 cursor-pointer shadow-2xs ${
                illuminatedKey === 'space' ? 'is-pressed' : ''
              }`}
              title="Mot suivant dans la phrase"
            >
              {illuminatedKey === 'space' && (
                <span className="keyboard-key-callout">␣ Espace</span>
              )}
              <span>␣</span>
              <span className="text-[11px] hidden xs:inline font-black">Espace</span>
            </button>
          )}

          {/* Backspace Button - Spacious & easy to hit */}
          <button
            type="button"
            onPointerDown={() => triggerKeyFeedback('backspace')}
            onClick={onBackspace}
            className={`flex-[1.8] sm:flex-[1.6] h-10 sm:h-11 md:h-12 bg-rose-50 hover:bg-rose-100 text-rose-900 text-xs sm:text-sm font-black rounded-lg border-2 border-rose-300 keyboard-key-illuminated flex items-center justify-center gap-1 cursor-pointer shadow-2xs ${
              illuminatedKey === 'backspace' ? 'is-pressed' : ''
            }`}
            title="Effacer"
          >
            {illuminatedKey === 'backspace' && (
              <span className="keyboard-key-callout">⌫ Effacer</span>
            )}
            <span className="text-base leading-none">⌫</span>
            <span className="text-[11px] hidden xs:inline font-black">Effacer</span>
          </button>
        </div>

        {/* ROW 4: DEDICATED AMAZIGH SPECIAL CHARACTERS RIBBON */}
        <div className="flex justify-center gap-0.5 sm:gap-1 w-full pt-0.5 bg-amber-100/60 p-1 rounded-xl border border-amber-300/80">
          {KEYBOARD_AMAZIGH_ROW.map((k) => (
            <button
              key={k}
              type="button"
              onPointerDown={() => triggerKeyFeedback(k)}
              onClick={() => onInputChar(k)}
              className={`flex-1 h-9 sm:h-10 md:h-11 bg-amber-200/90 hover:bg-amber-300 text-amber-950 font-mono font-black text-xs sm:text-base rounded-lg border-2 border-amber-400 keyboard-key-illuminated flex items-center justify-center cursor-pointer min-w-0 shadow-xs active:scale-95 ${
                illuminatedKey === k ? 'is-pressed' : ''
              }`}
              title={`Lettre amazighe ${k}`}
            >
              {illuminatedKey === k && (
                <span className="keyboard-key-callout">{k}</span>
              )}
              <span>{k}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }
);

CrosswordVirtualKeyboard.displayName = 'CrosswordVirtualKeyboard';
